<template>
  <div
    class="content-plan-card"
    role="button"
    tabindex="0"
    @click="!isCardInEditMode ? onViewDetails() : undefined"
    @keydown.enter.prevent="!isCardInEditMode ? onViewDetails() : undefined"
  >
    <div class="content-plan-card__main">
      <div class="content-plan-card__chips">
        <b-dropdown
          v-if="whiteGloveMissionControlAdminUi"
          aria-role="list"
          class="content-plan-card__mc-dropdown content-plan-card__mc-dropdown--page-type"
          @click.native.stop
        >
          <template #trigger>
            <button
              v-if="silo.pageType === 'new' || silo.pageType === 'existing'"
              type="button"
              class="content-plan-card__mc-trigger content-plan-card__mc-trigger--page-type"
            >
              <span>{{ pageTypeLabel }}</span>
              <i class="material-icons-outlined content-plan-card__mc-trigger__caret">expand_more</i>
            </button>
            <b-tooltip
              v-else-if="isMissionControlSession"
              label="Assign page type"
              type="is-dark"
            >
              <button
                type="button"
                class="content-plan-card__mc-trigger content-plan-card__mc-trigger--page-type content-plan-card__mc-trigger--page-type-assign"
              >
                <i class="material-icons-outlined" style="font-size: 1.125rem;">description</i>
                <i class="material-icons-outlined content-plan-card__mc-trigger__caret">expand_more</i>
              </button>
            </b-tooltip>
            <button
              v-else
              type="button"
              class="content-plan-card__mc-trigger content-plan-card__mc-trigger--page-type"
            >
              <span>Page type</span>
              <i class="material-icons-outlined content-plan-card__mc-trigger__caret">expand_more</i>
            </button>
          </template>
          <b-dropdown-item
            aria-role="listitem"
            @click.native.stop="updateSilo({ pageType: 'new' }, true)"
          >
            New page
          </b-dropdown-item>
          <b-dropdown-item
            aria-role="listitem"
            @click.native.stop="updateSilo({ pageType: 'existing' }, true)"
          >
            Existing page
          </b-dropdown-item>
        </b-dropdown>
        <span
          v-else-if="pageTypeLabel"
          class="content-plan-card__chip content-plan-card__chip--page-type"
        >{{ pageTypeLabel }}</span>
        <b-dropdown
          v-if="showFulfillmentStatusDropdown"
          aria-role="list"
          class="content-plan-card__mc-dropdown content-plan-card__mc-dropdown--status"
          @click.native.stop
        >
          <template #trigger>
            <button
              type="button"
              class="content-plan-card__mc-trigger"
              :class="statusDropdownTriggerClass"
            >
              <span>{{ silo.content_report_order.fulfillement_status === 'in_progress' ? 'Content in progress' : statusLabel }}</span>
              <i class="material-icons-outlined content-plan-card__mc-trigger__caret">expand_more</i>
            </button>
          </template>
          <b-dropdown-item
            v-if="silo.content_report_order.fulfillement_status !== 'pending'"
            aria-role="listitem"
            @click.native.stop="updateOrder({ set_pending: true })"
          >
            Mark as {{ getFulfillementStatusLabel('pending') }}
          </b-dropdown-item>
          <b-dropdown-item
            v-if="silo.content_report_order.fulfillement_status !== 'in_progress'"
            aria-role="listitem"
            @click.native.stop="updateOrder({ set_in_progress: true })"
          >
            Mark as {{ getFulfillementStatusLabel('in_progress') }}
          </b-dropdown-item>
          <b-dropdown-item
            v-if="silo.content_report_order.fulfillement_status !== 'completed'"
            aria-role="listitem"
            @click.native.stop="updateOrder({ set_completed: true })"
          >
            Mark as {{ getFulfillementStatusLabel('completed') }}
          </b-dropdown-item>
        </b-dropdown>
        <span
          v-else
          class="content-plan-card__chip"
          :class="statusChipClass"
        >{{ silo.content_report_order?.fulfillement_status === 'in_progress' ? 'Content in progress' : statusLabel }}</span>
        <b-dropdown
          v-if="showSalesTagDropdown"
          aria-role="list"
          class="content-plan-card__mc-dropdown content-plan-card__mc-dropdown--sales"
          @click.native.stop
        >
          <template #trigger>
            <button
              type="button"
              class="content-plan-card__mc-trigger"
              :class="salesTagTriggerClass"
            >
              <span>{{ salesTagLabel }}</span>
              <i class="material-icons-outlined content-plan-card__mc-trigger__caret">expand_more</i>
            </button>
          </template>
          <b-dropdown-item
            :disabled="silo.content_report_order.sales_tag === 'passive'"
            aria-role="listitem"
            @click.native.stop="updateOrder({ sales_tag: 'passive' })"
          >
            Set as passive
          </b-dropdown-item>
          <b-dropdown-item
            :disabled="silo.content_report_order.sales_tag === 'active'"
            aria-role="listitem"
            @click.native.stop="updateOrder({ sales_tag: 'active' })"
          >
            Set as <b>active</b>
          </b-dropdown-item>
        </b-dropdown>
        <span
          v-if="dueDateLabel"
          class="content-plan-card__due"
        >
          Content due {{ dueDateLabel }}
        </span>
      </div>

      <div class="content-plan-card__title-row">
        <h3 v-if="!isCardInEditMode" class="content-plan-card__title">
          <span
            v-if="whiteGloveMissionControlAdminUi"
            class="content-plan-card__title-editable"
            @click.stop="startEditingName"
          >
            {{ silo.name }}
          </span>
          <template v-else>
            {{ silo.name }}
          </template>
        </h3>
        <div
          v-else
          class="content-plan-card__title-editor"
          @click.stop
        >
          <v-text-field
            v-model="editedName"
            autofocus
            outlined
            dense
            hide-details
            class="content-plan-card__title-input"
            @blur="saveEditName"
            @keypress.enter="saveEditName"
          />
        </div>
        <div class="content-plan-card__title-actions">
          <v-menu
            v-if="hasOrderInfoPopover"
            open-on-hover
            :nudge-width="250"
            offset-y
            left
          >
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                small
                class="content-plan-card__info-btn"
                aria-label="Order details"
                v-bind="attrs"
                v-on="on"
                @click.stop
              >
                <span class="material-icons-outlined" style="font-size: 20px; color: #999999;">receipt_long</span>
              </v-btn>
            </template>
            <v-card class="pa-4 mainColor--text" style="max-width: 320px;">
              <div class="font-weight-bold mb-4" style="font-size: 1rem;">
                You have ordered {{ checkoutItemCount }} reports for ${{ silo.content_report_order.price_on_checkout }}
              </div>
              <div
                v-if="silo.content_report_order.order_status === 'to_be_paid_by_invoice'"
                class="font-weight-bold mb-4"
                style="color: var(--v-profileAnchorColor-base);"
              >
                Not yet paid. Invoice is due:
                {{ new Date(silo.content_report_order.stripe_invoice_to_be_finalized_at).toDateString() }}
              </div>
              <div
                v-for="(item, index) in silo.content_report_order.items_on_checkout || []"
                :key="index"
              >
                <template v-if="item.type === 'top_level'">
                  <div class="font-weight-bold mb-1">{{ item.quantity }} reports for Top-level keywords</div>
                  <div>{{ item.report_label }}</div>
                  <div class="mb-3">({{ item.quantity }}x ${{ item.unit_amount }} USD)</div>
                </template>
                <template v-else-if="item.type === 'supporting'">
                  <div class="font-weight-bold mb-1">{{ item.quantity }} reports for Supporting keywords</div>
                  <div>{{ item.report_label }}</div>
                  <div>({{ item.quantity }}x ${{ item.unit_amount }} USD)</div>
                </template>
                <template v-else-if="item.type === 'speedy_delivery'">
                  <div class="font-weight-bold mb-1">Speedy delivery</div>
                  <div>(${{ item.unit_amount }} USD)</div>
                </template>
              </div>
              <template v-if="silo.content_report_order.applied_coupon_type">
                <div class="font-weight-bold mb-1 mt-4">Applied coupon</div>
                <div>
                  {{ silo.content_report_order.applied_coupon_type === 'Amount Off' ? '$' : '' }}{{
                    silo.content_report_order.applied_coupon_amount_off
                  }}{{ silo.content_report_order.applied_coupon_type === 'Percent Off' ? '%' : '' }} off
                </div>
              </template>
            </v-card>
          </v-menu>
          <v-btn
            v-if="whiteGloveMissionControlAdminUi"
            icon
            small
            :id="`cp-menu-${silo.id}`"
            class="content-plan-card__more"
            aria-label="More actions"
            @click.stop
          >
            <span class="material-icons-outlined mainColor--text" style="font-size: 24px;">more_horiz</span>
          </v-btn>
          <v-menu v-if="whiteGloveMissionControlAdminUi" :activator="`#cp-menu-${silo.id}`" bottom offset-y>
            <v-list>
              <v-list-item @click="onViewDetailsMiddle">
                <v-list-item-title class="mainColor--text">Open in new tab</v-list-item-title>
              </v-list-item>
              <v-list-item @click="showDeleteConfirmModal = true">
                <v-list-item-title class="mainColor--text">Delete</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>

      <p class="content-plan-card__meta">
        <span class="content-plan-card__meta-label">Main page:</span>
        <span class="content-plan-card__meta-path">{{ topLevelKeywordDisplay }}</span>
        <span class="content-plan-card__meta-sep"> • </span>
        <span class="content-plan-card__meta-secondary">{{ supportingArticlesLine }}</span>
      </p>
    </div>

    <div class="content-plan-card__actions">
      <button
        type="button"
        class="content-plan-card__btn-action"
        @click.stop="onViewDetails"
      >
        {{ primaryActionLabel }}
      </button>
    </div>

    <div class="modal" :class="{ 'is-active': !!showDeleteConfirmModal }">
      <div class="modal-background" @click.stop="showDeleteConfirmModal = false"></div>
      <div class="modal-content" @click.stop>
        <div class="box modal-container" style="overflow: hidden">
          <div class="modal-content-root">
            <div class="title-font is-size-4 mainColor--text">
              You&apos;re about to delete this content plan
            </div>
            <div class="control flex mt20">
              <div class="flex-grow-1"></div>
              <v-btn
                rounded
                outlined
                color="mainColor"
                class="button-with-icon mainColor--text text-none"
                @click.stop="showDeleteConfirmModal = false"
              >
                <span>Cancel</span>
              </v-btn>
              <v-btn
                rounded
                color="profileAnchorColor"
                class="button-with-icon text-none whiteColor--text"
                @click.stop="deleteSilo"
              >
                <i class="fa fa-trash mr5"></i>
                <span>Delete</span>
              </v-btn>
            </div>
          </div>
        </div>
        <button
          class="custom-modal-close-btn"
          aria-label="close"
          @click.stop="showDeleteConfirmModal = false"
        >
          <i class="material-icons">close</i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { getPureServicePkgName } from '@/utils'
import { whiteGlovePlans } from '@/components/plans_constants'
import { getFulfillementStatusLabel } from '@/components/Campaigns/content-orders/utils.js'

/** Display text for the silo’s target keyword (top-level), not the connected page record. */
function topLevelKeywordLabel (silo) {
  // Server-computed label (available even when silo_keywords are not fetched)
  if (typeof silo.topLevelKeywordLabel === 'string' && silo.topLevelKeywordLabel) {
    return silo.topLevelKeywordLabel
  }
  const kws = (silo.silo_keywords || []).filter((k) => !k?.isDeleted)
  const primary = kws.find(
    (k) => k.keywordData?.isPrimaryKeyword && k.keywordData?.isTopLevelKeyword,
  )
  if (primary?.keyword?.trim()) return primary.keyword.trim()
  const top = kws.find(
    (k) => k.keywordData?.isTopLevelKeyword && !k.keywordData?.isSupportingKeyword,
  )
  if (top?.keyword?.trim()) return top.keyword.trim()
  const nonSupporting = kws.find((k) => !k.keywordData?.isSupportingKeyword)
  if (nonSupporting?.keyword?.trim()) return nonSupporting.keyword.trim()
  return 'Not set'
}

function supportingArticlesCount (silo) {
  // Server-computed count (available even when silo_keywords are not fetched)
  if (Number.isFinite(silo.supportingArticlesCount)) {
    return silo.supportingArticlesCount
  }
  const kws = silo.silo_keywords || []
  return kws.filter((k) => !k?.isDeleted && k?.keywordData?.isSupportingKeyword).length
}

export default {
  name: 'ContentPlanCard',
  props: {
    silo: {
      type: Object,
      required: true,
    },
    refreshData: {
      type: Function,
      required: true,
    },
  },
  inject: {
    refreshWhiteGloveOrders: {
      default: null,
    },
  },
  data () {
    return {
      isCardInEditMode: false,
      editedName: '',
      showDeleteConfirmModal: false,
    }
  },
  computed: {
    user () {
      return this.$store.state.user
    },
    isWhiteGloveUser () {
      if (!this.user) return false
      const accountType = this.$store.state.selectedSubAccountRelation
        ? this.$store.state.selectedSubAccountRelation.ownerAccountType
        : this.user.accountType
      return whiteGlovePlans.includes(getPureServicePkgName(accountType))
    },
    isMissionControlSession () {
      return Boolean(this.$store.state.selectedSubAccountRelation?.isMissionControlSubAccount)
    },
    isWhiteGloveAdminMode () {
      return this.$store.state.isWhiteGloveAdminMode
    },
    whiteGloveMissionControlAdminUi () {
      return this.isMissionControlSession && this.isWhiteGloveAdminMode
    },
    pageTypeLabel () {
      if (this.silo.pageType === 'new') return 'New page'
      if (this.silo.pageType === 'existing') return 'Existing page'
      return ''
    },
    orderedLike () {
      const st = this.silo?.content_report_order?.order_status
      return ['paid', 'to_be_paid_by_invoice'].includes(st)
    },
    hasOrderInfoPopover () {
      const o = this.silo?.content_report_order
      return (
        o &&
        ['paid', 'to_be_paid_by_invoice'].includes(o.order_status) &&
        o.price_on_checkout
      )
    },
    checkoutItemCount () {
      const items = this.silo.content_report_order?.items_on_checkout || []
      return items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0)
    },
    statusLabel () {
      const o = this.silo.content_report_order
      if (!o) return 'Ready to order'
      if (['in_cart', 'in_cart_checkout'].includes(o.order_status)) return 'In cart'
      if (o.order_status === 'user_editing') return 'Editing order'
      if (!this.orderedLike) return 'Ready to order'
      const fs = o.fulfillement_status
      return getFulfillementStatusLabel(fs) || fs || 'Ordered'
    },
    statusChipClass () {
      const o = this.silo?.content_report_order
      if (!o || !this.orderedLike) {
        if (o && ['in_cart', 'in_cart_checkout'].includes(o.order_status)) {
          return 'content-plan-card__chip--status-cart'
        }
        return 'content-plan-card__chip--status-ready'
      }
      if (o.fulfillement_status === 'completed') return 'content-plan-card__chip--status-done'
      if (o.fulfillement_status === 'in_progress') return 'content-plan-card__chip--status-progress'
      if (o.order_status === 'to_be_paid_by_invoice') return 'content-plan-card__chip--status-issue'
      return 'content-plan-card__chip--status-ordered'
    },
    dueDateLabel () {
      const o = this.silo?.content_report_order
      if (o?.order_status === 'paid' && o?.due_date) {
        return moment.utc(o.due_date).format('MMMM D')
      }
      return ''
    },
    topLevelKeywordDisplay () {
      return topLevelKeywordLabel(this.silo)
    },
    supportingArticlesLine () {
      const n = supportingArticlesCount(this.silo)
      return `${n} supporting ${n === 1 ? 'article' : 'articles'}`
    },
    primaryActionLabel () {
      const o = this.silo?.content_report_order
      return o?.order_status === 'paid' ? 'View details' : 'Order content'
    },
    showFulfillmentStatusDropdown () {
      return (
        this.isWhiteGloveUser &&
        this.whiteGloveMissionControlAdminUi &&
        this.silo?.content_report_order?.order_status === 'paid'
      )
    },
    showSalesTagDropdown () {
      return (
        this.isWhiteGloveUser &&
        this.whiteGloveMissionControlAdminUi &&
        !!this.silo?.content_report_order
      )
    },
    statusDropdownTriggerClass () {
      const fs = this.silo?.content_report_order?.fulfillement_status
      if (fs === 'completed') return 'content-plan-card__mc-trigger--status-done'
      if (fs === 'in_progress') return 'content-plan-card__mc-trigger--status-progress'
      return 'content-plan-card__mc-trigger--status-pending'
    },
    salesTagLabel () {
      const t = this.silo?.content_report_order?.sales_tag
      if (t === 'active') return 'Active'
      return 'Passive'
    },
    salesTagTriggerClass () {
      const t = this.silo?.content_report_order?.sales_tag
      if (t === 'active') return 'content-plan-card__mc-trigger--sales-active'
      return 'content-plan-card__mc-trigger--sales-passive'
    },
  },
  methods: {
    getFulfillementStatusLabel,
    shouldRefreshWhiteGloveSiteStatus (body) {
      return Boolean(body.set_pending || body.set_in_progress || body.set_completed)
    },
    contentPlanDetailLocation () {
      return {
        name: 'ContentPlanDetailWhiteGlove',
        params: { siteId: String(this.silo.siteId), siloId: String(this.silo.id) },
      }
    },
    onViewDetails () {
      this.$router.push(this.contentPlanDetailLocation())
    },
    onViewDetailsMiddle () {
      window.open(this.$router.resolve(this.contentPlanDetailLocation()).href, '_blank')
    },
    startEditingName () {
      this.editedName = this.silo.name
      this.isCardInEditMode = true
    },
    saveEditName () {
      if (this.editedName === this.silo.name) {
        return new Promise((resolve) => setTimeout(resolve, 300))
          .then(() => {
            this.isCardInEditMode = false
          })
      }

      return this.updateSilo({ name: this.editedName }, true)
        .then(() => new Promise((resolve) => setTimeout(resolve, 300)))
        .then(() => {
          this.isCardInEditMode = false
        })
    },
    updateOrder (payload) {
      if (!this.silo.content_report_order?.id) return
      this.$store.commit('showLoading')
      this.$store
        .dispatch('updateSiloContentReportOrder', {
          orderId: this.silo.content_report_order.id,
          body: payload,
        })
        .then((response) => {
          if (response.data && response.data.status === 'SUCCESS') {
            const refreshTasks = [this.refreshData()]
            if (
              this.shouldRefreshWhiteGloveSiteStatus(payload)
              && typeof this.refreshWhiteGloveOrders === 'function'
            ) {
              refreshTasks.push(this.refreshWhiteGloveOrders())
            }
            return Promise.all(refreshTasks)
          }
        })
        .finally(() => {
          this.$store.commit('hideLoading')
        })
    },
    deleteSilo () {
      this.$store.commit('showLoading')
      return this.$store
        .dispatch('deleteSilo', this.silo.id)
        .then(() => {
          this.showDeleteConfirmModal = false
          return this.refreshData()
        })
        .finally(() => {
          this.$store.commit('hideLoading')
        })
    },
    updateSilo (payload, refresh = false) {
      this.$store.commit('showLoading')
      return this.$store
        .dispatch('updateSilo', {
          siloId: this.silo.id,
          body: payload,
        })
        .then(() => {
          if (refresh) {
            this.refreshData()
          }
        })
        .finally(() => {
          this.$store.commit('hideLoading')
        })
    },
  },
}
</script>

<style lang="scss" scoped>
.content-plan-card {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: 20px;
  padding: 20px;
  background: #fff;
  border: 1px solid var(--v-gray12Color-base);
  border-radius: 6px;
  cursor: pointer;
  text-align: left;
  transition: box-shadow 0.15s ease;

  &:hover {
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.06);
  }

  &:focus {
    outline: 2px solid var(--v-profileAnchorColor-base);
    outline-offset: 2px;
  }
}

.content-plan-card__main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.content-plan-card__title-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 8px;
}

.content-plan-card__title {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--v-mainColor-base);
  line-height: 1.3;
  flex: 0 1 auto;
  min-width: 0;
}

.content-plan-card__title-editable {
  cursor: text;
  font-size: 1.05rem;
}

.content-plan-card__title-editor {
  flex: 1 1 260px;
  min-width: 0;
  max-width: 100%;
}

.content-plan-card__title-input {
  width: 100%;
  max-width: 420px;
}

.content-plan-card__doc-icon {
  flex-shrink: 0;
  font-size: 1.25rem;
  color: #999999;
}

.content-plan-card__title-actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  position: relative;
}

.content-plan-card__info-btn {
  margin: -4px 0;
  color:#999999;
}

.content-plan-card__more {
  margin: -4px -6px -4px 0;
}

.content-plan-card__meta {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.45;
}

.content-plan-card__meta-label {
  color: var(--v-mainColor-base);
  font-weight: 600;
}

.content-plan-card__meta-path {
  margin-left: 4px;
  color: #6A6A6A;
}

.content-plan-card__meta-sep {
  color: #6A6A6A;
}

.content-plan-card__meta-secondary {
  color: #6A6A6A;
}

.content-plan-card__chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

::v-deep .content-plan-card__mc-dropdown .dropdown-trigger {
  display: inline-flex;
}

::v-deep .content-plan-card__mc-dropdown .dropdown-menu {
  min-width: 200px;
}

.content-plan-card__mc-trigger {
  min-width: 108px;
  height: 28px;
  padding: 0 10px 0 12px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: #fff;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  line-height: 1.2;
}

.content-plan-card__mc-dropdown--status .content-plan-card__mc-trigger {
  min-width: 128px;
}

.content-plan-card__mc-dropdown--page-type .content-plan-card__mc-trigger--page-type {
  min-width: 108px;
  background: #f7f5fb;
  border-color: #e8e4f0;
  color: #6a6a6a;
  font-weight: 600;
}

.content-plan-card__mc-trigger--page-type-assign {
  min-width: 44px;
  padding: 0 6px 0 8px;
  border-style: dashed;
  border-color: #ccc;
  background: #fff;
  color: #6a6a6a;
}

::v-deep .content-plan-card__mc-dropdown--page-type .dropdown-item {
  font-weight: normal;
}

::v-deep .content-plan-card__mc-dropdown--page-type.is-disabled {
  opacity: initial;
  cursor: default;
}

.content-plan-card__mc-trigger__caret {
  flex-shrink: 0;
  font-size: 1.125rem !important;
  color: rgba(0, 0, 0, 0.45);
}

.content-plan-card__mc-trigger--status-progress {
  color: #EF34A4;
}

.content-plan-card__mc-trigger--status-done {
  color: #11A33D;
}

.content-plan-card__mc-trigger--status-pending {
  color: #FF5700;
}

.content-plan-card__mc-trigger--sales-active {
  color: var(--v-mainColor-base);
}

.content-plan-card__mc-trigger--sales-passive {
  color: var(--v-mainColor-base);
}

.content-plan-card__chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.2;
}

.content-plan-card__chip--page-type {
  background: #F7F5FB;
  color: #6A6A6A;
}

.content-plan-card__chip--status-ready {
  background: #ede7f6;
  color: #5e35b1;
}

.content-plan-card__chip--status-cart {
  background: #fff3e0;
  color: #e65100;
}

.content-plan-card__chip--status-ordered {
  background: #FF57001A;
  color: #FF5700;
}

.content-plan-card__chip--status-progress {
  background: #EF34A41A;
  color: #EF34A4;
}

.content-plan-card__chip--status-done {
  background: #34A8531A;
  color: #11A33D;
}

.content-plan-card__chip--status-issue {
  background: #ffebee;
  color: #c62828;
}

.content-plan-card__due {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--v-mainColor-base);
  line-height: 1.2;
  white-space: nowrap;
}

.content-plan-card__actions {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  flex-shrink: 0;
  gap: 16px;
  padding: 0;
  border: none;
  margin: 0;
  /* Keep the CTA width consistent across cards. */
  min-width: 142px;
}

.content-plan-card__btn-action {
  width: 100%;
  padding: 8px 18px;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  border: 2px solid var(--v-profileAnchorColor-base);
  background: #fff;
  color: var(--v-profileAnchorColor-base);

  &:hover {
    opacity: 0.9;
    background: #fffaf5;
  }
}
</style>
